import { useState } from 'react'
import {
  Card, Table, Button, Tag, Space, Typography, Form, Select, DatePicker,
  Input, InputNumber, Row, Col, Drawer, message, Tabs, Badge,
} from 'antd'
import {
  PlusOutlined, EditOutlined, WarningOutlined, CustomerServiceOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { capaApi } from '@/features/reports/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography

const CAPA_SOURCES = [
  'QUALITY_TEST', 'CUSTOMER_COMPLAINT', 'AUDIT', 'MACHINE_BREAKDOWN',
  'WASTE_DEVIATION', 'SUPPLIER_DEVIATION', 'PROCESS_DEVIATION',
  'INTERNAL_AUDIT', 'MANAGEMENT_REVIEW', 'REGULATORY', 'NEAR_MISS',
  'PREVENTIVE', 'CUSTOMER_FEEDBACK', 'EXTERNAL_AUDIT', 'OTHER',
]

const SEVERITY_COLORS: Record<string, string> = {
  LOW: 'blue', MEDIUM: 'gold', HIGH: 'orange', CRITICAL: 'red',
}
const STATUS_COLORS: Record<string, string> = {
  OPEN: 'red', IN_PROGRESS: 'blue', OVERDUE: 'volcano',
  PENDING_VERIFICATION: 'gold', CLOSED: 'green', CANCELLED: 'default',
}
const COMPLAINT_STATUS_COLORS: Record<string, string> = {
  OPEN: 'red', 'UNDER_INVESTIGATION': 'orange', RESOLVED: 'green',
  CLOSED: 'default', REJECTED: 'purple',
}

export default function CAPAPage() {
  const { plantId } = usePlantId()
  const qc = useQueryClient()

  const [openCapa, setOpenCapa] = useState(false)
  const [openComplaint, setOpenComplaint] = useState(false)
  const [editingCapa, setEditingCapa] = useState<Record<string, unknown> | null>(null)
  const [editingComplaint, setEditingComplaint] = useState<Record<string, unknown> | null>(null)
  const [capaForm] = Form.useForm()
  const [complaintForm] = Form.useForm()

  const { data: capas, isLoading: capaLoading } = useQuery({
    queryKey: ['capas-list', plantId],
    queryFn: () => capaApi.listCapas({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  const { data: complaints, isLoading: complaintLoading } = useQuery({
    queryKey: ['complaints-list', plantId],
    queryFn: () => capaApi.listComplaints({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  const createCapaMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => capaApi.createCapa(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['capas-list'] }); message.success('CAPA created'); setOpenCapa(false); capaForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const updateCapaMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => capaApi.updateCapa(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['capas-list'] }); message.success('CAPA updated'); setOpenCapa(false); setEditingCapa(null); capaForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Update failed'),
  })

  const createComplaintMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => capaApi.createComplaint(d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['complaints-list'] }); message.success('Complaint registered'); setOpenComplaint(false); complaintForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Save failed'),
  })

  const updateComplaintMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => capaApi.updateComplaint(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['complaints-list'] }); message.success('Updated'); setOpenComplaint(false); setEditingComplaint(null); complaintForm.resetFields() },
    onError: (e: unknown) => message.error((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Update failed'),
  })

  const capaColumns = [
    { title: 'CAPA No', dataIndex: 'capa_no', key: 'no', width: 130 },
    { title: 'Initiated', dataIndex: 'initiated_date', key: 'date', width: 110 },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 150, render: (v: string) => v?.replace('_', ' '), ellipsis: true },
    {
      title: 'Severity', dataIndex: 'severity', key: 'sev', width: 90,
      render: (v: string) => <Tag color={SEVERITY_COLORS[v] || 'default'}>{v}</Tag>
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 140,
      render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v?.replace('_', ' ')}</Tag>
    },
    { title: 'Problem', dataIndex: 'problem_description', key: 'problem', ellipsis: true },
    { title: 'Target Date', dataIndex: 'target_date', key: 'target', width: 110 },
    {
      title: 'Days Open', dataIndex: 'days_open', key: 'days', width: 90, align: 'right' as const,
      render: (v: number) => <span style={{ color: v > 30 ? '#cf1322' : v > 14 ? '#d46b08' : 'inherit' }}>{v}</span>
    },
    {
      title: '', key: 'actions', width: 60,
      render: (_: unknown, r: Record<string, unknown>) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => {
          setEditingCapa(r)
          capaForm.setFieldsValue({
            ...r,
            target_date: r.target_date ? dayjs(r.target_date as string) : undefined,
            closure_date: r.closure_date ? dayjs(r.closure_date as string) : undefined,
          })
          setOpenCapa(true)
        }} />
      ),
    },
  ]

  const complaintColumns = [
    { title: 'Complaint No', dataIndex: 'complaint_no', key: 'no', width: 130 },
    { title: 'Received', dataIndex: 'received_date', key: 'date', width: 110 },
    { title: 'Customer', dataIndex: 'customer_name', key: 'cust', width: 150, ellipsis: true },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot', width: 100 },
    { title: 'Type', dataIndex: 'complaint_type', key: 'type', width: 130, render: (v: string) => v?.replace('_', ' ') },
    { title: 'Qty (kg)', dataIndex: 'qty_complained_kg', key: 'qty', align: 'right' as const, width: 90 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (v: string) => <Tag color={COMPLAINT_STATUS_COLORS[v] || 'default'}>{v?.replace('_', ' ')}</Tag>
    },
    { title: 'Disposition', dataIndex: 'disposition', key: 'disp', width: 100 },
    { title: 'Debit Note', dataIndex: 'debit_note_amount', key: 'debit', align: 'right' as const, width: 100 },
    { title: 'CAPA No', dataIndex: 'capa_no', key: 'capa', width: 110 },
    {
      title: '', key: 'actions', width: 60,
      render: (_: unknown, r: Record<string, unknown>) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => {
          setEditingComplaint(r)
          complaintForm.setFieldsValue({
            ...r,
            received_date: r.received_date ? dayjs(r.received_date as string) : undefined,
            closure_date: r.closure_date ? dayjs(r.closure_date as string) : undefined,
          })
          setOpenComplaint(true)
        }} />
      ),
    },
  ]

  // Count overdue from open CAPAs
  const overdueCount = Array.isArray(capas)
    ? capas.filter((c: Record<string, unknown>) => c.is_overdue || c.status === 'OVERDUE').length
    : 0

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <WarningOutlined style={{ marginRight: 8, color: '#faad14' }} />
        CAPA & Customer Complaints
      </Title>

      <Tabs
        defaultActiveKey="capa"
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'capa',
            label: (
              <span>
                CAPA Records
                {overdueCount > 0 && <Badge count={overdueCount} size="small" style={{ marginLeft: 8 }} />}
              </span>
            ),
            children: (
              <Card
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingCapa(null)
                    capaForm.resetFields()
                    capaForm.setFieldsValue({ severity: 'MEDIUM' })
                    setOpenCapa(true)
                  }}>
                    New CAPA
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(capas) ? capas : []}
                  columns={capaColumns}
                  rowKey="id"
                  loading={capaLoading}
                  size="small"
                  scroll={{ x: 1100 }}
                  pagination={{ pageSize: 20 }}
                  rowClassName={(r: Record<string, unknown>) =>
                    (r.is_overdue || r.status === 'OVERDUE') ? 'ant-table-row-danger' : ''
                  }
                />
              </Card>
            ),
          },
          {
            key: 'complaints',
            label: (
              <span>
                <CustomerServiceOutlined style={{ marginRight: 4 }} />
                Customer Complaints
                {Array.isArray(complaints) && complaints.filter((c: Record<string, unknown>) => c.status === 'OPEN').length > 0 && (
                  <Badge
                    count={complaints.filter((c: Record<string, unknown>) => c.status === 'OPEN').length}
                    size="small"
                    style={{ marginLeft: 8 }}
                  />
                )}
              </span>
            ),
            children: (
              <Card
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingComplaint(null)
                    complaintForm.resetFields()
                    complaintForm.setFieldsValue({ received_date: dayjs() })
                    setOpenComplaint(true)
                  }}>
                    Register Complaint
                  </Button>
                }
              >
                <Table
                  dataSource={Array.isArray(complaints) ? complaints : []}
                  columns={complaintColumns}
                  rowKey="id"
                  loading={complaintLoading}
                  size="small"
                  scroll={{ x: 1200 }}
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* CAPA Drawer */}
      <Drawer
        title={editingCapa ? `Edit CAPA — ${editingCapa.capa_no}` : 'New CAPA Record'}
        open={openCapa}
        onClose={() => { setOpenCapa(false); setEditingCapa(null); capaForm.resetFields() }}
        width={640}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpenCapa(false); setEditingCapa(null); capaForm.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={createCapaMut.isPending || updateCapaMut.isPending}
              onClick={() => capaForm.validateFields().then(vals => {
                const payload = {
                  ...vals,
                  plant_id: plantId,
                  target_date: vals.target_date?.format('YYYY-MM-DD'),
                  closure_date: vals.closure_date?.format('YYYY-MM-DD'),
                }
                if (editingCapa) updateCapaMut.mutate({ id: editingCapa.id as string, data: payload })
                else createCapaMut.mutate(payload)
              })}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={capaForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="source" label="Source" rules={[{ required: !editingCapa }]}>
                <Select disabled={!!editingCapa}>
                  {CAPA_SOURCES.map(s => <Select.Option key={s} value={s}>{s.replace('_', ' ')}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
                <Select>
                  {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map(s => (
                    <Select.Option key={s} value={s}><Tag color={SEVERITY_COLORS[s]}>{s}</Tag></Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="source_reference" label="Source Reference (e.g. LAB-2025-0012)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="target_date" label="Target Closure Date">
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="problem_description" label="Problem Description" rules={[{ required: !editingCapa }]}>
            <Input.TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="root_cause_method" label="Root Cause Method">
                <Select allowClear>
                  {['5WHY', 'FISHBONE', '8D', 'BRAINSTORM', 'OTHER'].map(m => (
                    <Select.Option key={m} value={m}>{m}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select allowClear>
                  {['OPEN', 'IN_PROGRESS', 'PENDING_VERIFICATION', 'CLOSED', 'CANCELLED'].map(s => (
                    <Select.Option key={s} value={s}><Tag color={STATUS_COLORS[s]}>{s.replace('_', ' ')}</Tag></Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="root_cause" label="Root Cause">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="corrective_action" label="Corrective Action">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="preventive_action" label="Preventive Action">
            <Input.TextArea rows={2} />
          </Form.Item>

          {editingCapa && (
            <>
              <Form.Item name="closure_date" label="Closure Date">
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="closure_remarks" label="Closure Remarks">
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}
        </Form>
      </Drawer>

      {/* Complaint Drawer */}
      <Drawer
        title={editingComplaint ? `Edit — ${editingComplaint.complaint_no}` : 'Register Customer Complaint'}
        open={openComplaint}
        onClose={() => { setOpenComplaint(false); setEditingComplaint(null); complaintForm.resetFields() }}
        width={560}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setOpenComplaint(false); setEditingComplaint(null); complaintForm.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={createComplaintMut.isPending || updateComplaintMut.isPending}
              onClick={() => complaintForm.validateFields().then(vals => {
                const payload = {
                  ...vals,
                  plant_id: plantId,
                  received_date: vals.received_date?.format('YYYY-MM-DD'),
                  closure_date: vals.closure_date?.format('YYYY-MM-DD'),
                }
                if (editingComplaint) updateComplaintMut.mutate({ id: editingComplaint.id as string, data: payload })
                else createComplaintMut.mutate(payload)
              })}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={complaintForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="customer_name" label="Customer Name" rules={[{ required: !editingComplaint }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="received_date" label="Received Date" rules={[{ required: !editingComplaint }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lot_no" label="Lot No"><Input /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="complaint_type" label="Complaint Type">
                <Select allowClear>
                  {['COUNT_VARIATION','STRENGTH_FAILURE','HAIRINESS','NEPS','THIN_THICK_PLACES','CONTAMINATION','PACKING','SHORT_DELIVERY','OTHER'].map(t => (
                    <Select.Option key={t} value={t}>{t.replace('_', ' ')}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="qty_complained_kg" label="Qty Complained (kg)">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="complaint_description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          {editingComplaint && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="status" label="Status">
                    <Select>
                      {['OPEN','UNDER_INVESTIGATION','RESOLVED','CLOSED','REJECTED'].map(s => (
                        <Select.Option key={s} value={s}>{s.replace('_', ' ')}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="disposition" label="Disposition">
                    <Select allowClear>
                      {['ACCEPT','REPLACE','CREDIT','REJECT','PENDING'].map(d => (
                        <Select.Option key={d} value={d}>{d}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="debit_note_amount" label="Debit Note Amount">
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="capa_no" label="CAPA No"><Input /></Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="closure_date" label="Closure Date">
                    <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="investigation_findings" label="Investigation Findings">
                <Input.TextArea rows={3} />
              </Form.Item>
            </>
          )}
        </Form>
      </Drawer>
    </div>
  )
}
